import React, { useState } from 'react';

function DebugModal({ isOpen, onClose, data }) {
  const [activeTab, setActiveTab] = useState('summary');
  
  if (!isOpen) return null;
  
  const tabs = [
    { id: 'summary', label: 'Summary', icon: '📊' },
    { id: 'requisition', label: 'Requisition', icon: '📄' },
    { id: 'users', label: 'Users & Permissions', icon: '👤' },
    { id: 'submissions', label: 'Submissions/Quotes', icon: '💰' },
    { id: 'comments', label: 'Comments & Files', icon: '💬' },
    { id: 'raw', label: 'Raw Data', icon: '🔧' }
  ];
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center pb-3 border-b">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                🐛 Debug Data - EPR-{data.requisition?.requisition_number || 'Unknown'}
              </h3>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mt-4">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="mt-4 overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
              
              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Basic Info */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-3">Basic Information</h4>
                      <div className="space-y-2 text-sm">
                        <div><strong>EPR Number:</strong> {data.requisition?.requisition_number || 'N/A'}</div>
                        <div><strong>Title:</strong> {data.requisition?.specification_title || 'N/A'}</div>
                        <div><strong>Requestor:</strong> {data.requisition?.user_full_name || 'N/A'}</div>
                        <div><strong>Department:</strong> {data.requisition?.department_name || 'N/A'}</div>
                        <div><strong>Total Amount:</strong> R{(data.requisition?.requisition_total || data.requisition?.quotation_total || 0).toLocaleString()}</div>
                        <div><strong>Status:</strong> {data.requisition?.requisition_status || 'N/A'}</div>
                        <div><strong>GL Code:</strong> {data.requisition?.gl_code || 'N/A'}</div>
                      </div>
                    </div>

                    {/* Approval Status */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-3">Approval Status</h4>
                      <div className="space-y-2 text-sm">
                        <div><strong>Approvals:</strong> {data.approvals?.length || 0}/2</div>
                        <div><strong>Comments:</strong> {data.comments?.length || 0}</div>
                        <div><strong>Files:</strong> {data.files?.length || 0}</div>
                        <div><strong>Quote Files:</strong> {data.quote_files || 0}</div>
                        <div><strong>Assigned To:</strong> {data.assigned || 'Not assigned'}</div>
                        <div><strong>Emergency:</strong> {data.requisition?.requisition_emergency ? 'Yes' : 'No'}</div>
                        <div><strong>Capex:</strong> {data.requisition?.requisition_capex || 'N/A'}</div>
                      </div>
                    </div>

                    {/* Procurement Info */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-900 mb-3">Procurement Information</h4>
                      <div className="space-y-2 text-sm">
                        <div><strong>Procurement ID:</strong> {data.requisition?.procurement_id || 'N/A'}</div>
                        <div><strong>Selected Quote ID:</strong> {data.requisition?.requisition_quote || 'N/A'}</div>
                        <div><strong>Submissions:</strong> {data.submissions?.length || 0}</div>
                        <div><strong>Selected Supplier:</strong> {data.selected?.quotation_company_name || 'N/A'}</div>
                        <div><strong>RFQ Sent:</strong> {data.sent?.length || 0} suppliers</div>
                      </div>
                    </div>

                    {/* Current User Info */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-3">Current User Context</h4>
                      <div className="space-y-2 text-sm">
                        <div><strong>User ID:</strong> {data.current_user?.user_id || 'N/A'}</div>
                        <div><strong>Username:</strong> {data.current_user?.user_full_name || 'N/A'}</div>
                        <div><strong>Department:</strong> {data.user_department || 'N/A'}</div>
                        <div><strong>Role:</strong> {data.user_role || 'N/A'}</div>
                        <div><strong>Permissions:</strong> {data.user_permissions?.length || 0}</div>
                        <div><strong>Session Token:</strong> {data.session_token ? '✓' : '✗'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Requisition Tab */}
              {activeTab === 'requisition' && (
                <div className="space-y-4">
                  <div className="bg-gray-100 p-4 rounded-md">
                    <h4 className="font-semibold text-gray-900 mb-2">Full Requisition Data</h4>
                    <pre className="text-xs overflow-auto max-h-96 bg-white p-3 rounded border">{JSON.stringify(data.requisition, null, 2)}</pre>
                  </div>
                  
                  {/* Items Table */}
                  {data.requisition?.specification_items && (
                    <div className="bg-gray-100 p-4 rounded-md">
                      <h4 className="font-semibold text-gray-900 mb-2">Requisition Items</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Qty</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {Array.isArray(data.requisition.specification_items) && data.requisition.specification_items.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2 text-xs">{index + 1}</td>
                                <td className="px-3 py-2 text-xs">{item || 'N/A'}</td>
                                <td className="px-3 py-2 text-xs">
                                  {(Array.isArray(data.requisition.specification_qty) && data.requisition.specification_qty[index]) || 'N/A'}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  {(Array.isArray(data.requisition.requisition_amounts) && data.requisition.requisition_amounts[index]) || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Users & Permissions Tab */}
              {activeTab === 'users' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Current User */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Current User</h4>
                      <pre className="text-xs overflow-auto max-h-40 bg-white p-2 rounded">{JSON.stringify(data.current_user, null, 2)}</pre>
                    </div>

                    {/* User Permissions */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">User Permissions ({data.user_permissions?.length || 0})</h4>
                      <div className="bg-white p-2 rounded max-h-40 overflow-auto">
                        {data.user_permissions?.map((permission, index) => (
                          <div key={index} className="text-xs py-1 border-b border-gray-100 last:border-b-0">
                            {permission}
                          </div>
                        )) || <div className="text-xs text-gray-500">No permissions found</div>}
                      </div>
                    </div>

                    {/* Approvers */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-900 mb-2">Approvers & Managers</h4>
                      <div className="space-y-2 text-xs">
                        <div><strong>Mid Manager:</strong> {data.mid_manager?.user_full_name || 'N/A'}</div>
                        <div><strong>Second Approval:</strong> {data.second_approval?.user_full_name || 'N/A'}</div>
                        <div><strong>SAP Approval:</strong> {data.sap_approval?.user_full_name || 'N/A'}</div>
                        <div><strong>App Specification:</strong> {data.app_specification?.user_full_name || 'N/A'}</div>
                        <div><strong>App Requisition:</strong> {data.app_requisition?.user_full_name || 'N/A'}</div>
                      </div>
                    </div>

                    {/* Delegates */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2">Delegates</h4>
                      <div className="space-y-2 text-xs">
                        <div><strong>Mid Delegate:</strong> {data.mid_delegate?.user_full_name || 'None'}</div>
                        <div><strong>Second Delegate:</strong> {data.second_delegate?.user_full_name || 'None'}</div>
                        <div><strong>SAP Delegate:</strong> {data.sap_delegate?.user_full_name || 'None'}</div>
                      </div>
                    </div>
                  </div>

                  {/* All Users */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">All Users ({data.all_users?.length || 0})</h4>
                    <div className="max-h-40 overflow-auto">
                      <pre className="text-xs bg-white p-2 rounded">{JSON.stringify(data.all_users, null, 2)}</pre>
                    </div>
                  </div>

                  {/* Buyers */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Buyers ({data.buyers?.length || 0})</h4>
                    <div className="max-h-40 overflow-auto">
                      <pre className="text-xs bg-white p-2 rounded">{JSON.stringify(data.buyers, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              )}

              {/* Submissions/Quotes Tab */}
              {activeTab === 'submissions' && (
                <div className="space-y-4">
                  {/* Selected Quote */}
                  {data.selected && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-3">Selected Quote</h4>
                      
                      {/* Quote Directory Info */}
                      <div className="bg-green-100 p-3 rounded mb-3">
                        <h5 className="font-medium text-green-800 mb-2">📁 Quote Directory Information</h5>
                        <div className="space-y-1 text-sm">
                          <div><strong>Directory:</strong> <code className="bg-white px-2 py-1 rounded text-xs">{data.selected.quotation_directory || 'N/A'}</code></div>
                          <div><strong>Company:</strong> {data.selected.quotation_company_name || 'N/A'}</div>
                          <div><strong>Quote ID:</strong> {data.selected.quotation_id || 'N/A'}</div>
                          <div><strong>Procurement ID:</strong> {data.selected.quotation_procurement_id || 'N/A'}</div>
                          <div><strong>Files Count:</strong> {data.selected.files?.length || 0}</div>
                          {data.selected.files && data.selected.files.length > 0 && (
                            <div>
                              <strong>Files:</strong>
                              <ul className="ml-4 mt-1">
                                {data.selected.files.map((file, index) => (
                                  <li key={index} className="text-xs">
                                    📄 {file.quotation_file_name || file.quotation_file || 'Unknown file'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <pre className="text-xs overflow-auto max-h-40 bg-white p-2 rounded">{JSON.stringify(data.selected, null, 2)}</pre>
                    </div>
                  )}

                  {/* All Submissions */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">All Submissions ({data.submissions?.length || 0})</h4>
                    <div className="max-h-96 overflow-auto">
                      <pre className="text-xs bg-white p-2 rounded">{JSON.stringify(data.submissions, null, 2)}</pre>
                    </div>
                  </div>

                  {/* Sent RFQs */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Sent RFQs ({data.sent?.length || 0})</h4>
                    <div className="max-h-40 overflow-auto">
                      <pre className="text-xs bg-white p-2 rounded">{JSON.stringify(data.sent, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              )}

              {/* Comments & Files Tab */}
              {activeTab === 'comments' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Comments */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Comments ({data.comments?.length || 0})</h4>
                      <div className="max-h-60 overflow-auto">
                        <pre className="text-xs bg-white p-2 rounded">{JSON.stringify(data.comments, null, 2)}</pre>
                      </div>
                    </div>

                    {/* Files */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">Files ({data.files?.length || 0})</h4>
                      <div className="max-h-60 overflow-auto">
                        <pre className="text-xs bg-white p-2 rounded">{JSON.stringify(data.files, null, 2)}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Approvals */}
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-yellow-900 mb-2">Approvals ({data.approvals?.length || 0})</h4>
                    <div className="bg-white p-2 rounded">
                      {data.approvals?.map((approval, index) => (
                        <div key={index} className="text-xs py-1 border-b border-gray-100 last:border-b-0">
                          User ID: {approval}
                        </div>
                      )) || <div className="text-xs text-gray-500">No approvals found</div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Raw Data Tab */}
              {activeTab === 'raw' && (
                <div className="space-y-4">
                  <div className="bg-gray-100 p-4 rounded-md">
                    <h4 className="font-semibold text-gray-900 mb-2">Complete Raw Data Object</h4>
                    <pre className="text-xs overflow-auto max-h-96 bg-white p-3 rounded border">{JSON.stringify(data, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebugModal;
